import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";

export const useAdminStatus = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Memoized check admin status function
  const checkAdminStatus = useCallback(async () => {
    if (authLoading) return;
    
    if (!user) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      // Use secure admin role checking function to prevent tampering
      const { data, error } = await supabase
        .rpc('get_current_user_role');

      if (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        // Log failed admin access attempt
        supabase.rpc('log_admin_access_attempt', { 
          attempted_action: 'check_admin_status',
          target_user_id: user.id 
        });
      } else {
        const isAdminUser = data === 'admin';
        setIsAdmin(isAdminUser);
        
        // Log successful admin access if user is admin
        if (isAdminUser) {
          supabase.rpc('log_admin_access_attempt', { 
            attempted_action: 'admin_status_verified',
            target_user_id: user.id 
          });
        }
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      // Log failed admin access attempt
      supabase.rpc('log_admin_access_attempt', { 
        attempted_action: 'check_admin_status_error',
        target_user_id: user.id 
      });
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  // Memoized return value to prevent unnecessary re-renders
  const result = useMemo(() => ({
    isAdmin,
    loading: loading || authLoading
  }), [isAdmin, loading, authLoading]);

  return result;
};